<script lang="ts">
import type { FunctionalComponent } from "vue"
import { CloseRingIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { computed, toRef } from "vue"

export enum PageBannerType {
  WARNING,
  ERROR,
  TIP,
  INFO,
  HELP,
}
</script>

<script setup lang="ts">
const props = withDefaults(defineProps<Props>(), {
  closeable: true,
})

const emit = defineEmits<Emits>()

interface Props {
  type: PageBannerType
  /**
   * Override the defaul label
   */
  label?: string
  icon?: FunctionalComponent
  title: string
  closeable?: boolean
}

interface Emits {
  (event: "close"): void
}

function messageLabel(type: PageBannerType) {
  const labelMap = new Map([
    [PageBannerType.WARNING, "Important"],
    [PageBannerType.ERROR, "Oops"],
    [PageBannerType.TIP, "Quick Tip"],
    [PageBannerType.INFO, "Info"],
    [PageBannerType.HELP, "Getting Started"],
  ])
  return labelMap.get(type) ?? ""
}

const label = toRef(props, "label", messageLabel(props.type))

function handleClose() {
  emit("close")
}

const labelStyle = computed(() => {
  return {
    "bg-orange-300": props.type === PageBannerType.WARNING,
    "bg-red-700 text-white": props.type === PageBannerType.ERROR,
    "bg-teal-700 text-white": props.type === PageBannerType.TIP,
    "bg-blue-600 text-white": props.type === PageBannerType.INFO,
    "bg-gray-300": props.type === PageBannerType.HELP,
  }
})

const wrapperStyle = computed(() => {
  return {
    "border-orange-300": props.type === PageBannerType.WARNING,
    "border-red-700": props.type === PageBannerType.ERROR,
    "border-teal-700": props.type === PageBannerType.TIP,
    "bg-blue-100 border-transparent": props.type === PageBannerType.INFO,
    "border-gray-300": props.type === PageBannerType.HELP,
  }
})
</script>

<template>
  <div class="flex h-auto w-full flex-col items-center sm:relative sm:flex-row">
    <div
      class="z-1 flex w-40 items-center justify-center gap-2 rounded-full px-3 py-1.5 font-bold sm:absolute sm:left-0"
      :class="labelStyle"
    >
      <component
        :is="icon"
        v-if="icon"
        class="h-4 w-4"
      />
      <span>{{ label }}</span>
    </div>
    <div
      class="relative -mt-3 flex w-full flex-1 rounded-lg border shadow-md sm:ml-20 sm:mt-0 sm:pl-20"
      :class="wrapperStyle"
    >
      <div class="space-y-2 px-2 py-7 sm:px-8 sm:py-4">
        <p class="font-bold">
          {{ title }}
        </p>
        <div class="text-sm">
          <slot />
        </div>
      </div>
      <button
        v-if="closeable"
        class="absolute right-2 top-2 flex items-center gap-2 hover:opacity-50"
        @click="handleClose"
      >
        <span class="text-sm">Close</span>
        <CloseRingIcon class="h-4 w-4" />
      </button>
    </div>
  </div>
</template>
